/**
 * LOT-UI-02 — Statut caisse du magasin session (header).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Unlock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useActiveMagasin from '../../hooks/useActiveMagasin';
import { shouldShowCaisseInHeader } from '../../utils/roleRouting';

const CaisseHeaderChip = () => {
  const { api, user } = useAuth();
  const { code, isAll } = useActiveMagasin();
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!api || !user || !shouldShowCaisseInHeader(user)) return undefined;
    let cancelled = false;
    const fetchStatus = async () => {
      setLoading(true);
      try {
        if (isAll) {
          const [m1, m2] = await Promise.all([
            api.get('/caisse/status?magasin_id=M1').catch(() => ({ data: {} })),
            api.get('/caisse/status?magasin_id=M2').catch(() => ({ data: {} })),
          ]);
          if (!cancelled) {
            setOpen({
              M1: !!m1.data?.is_open,
              M2: !!m2.data?.is_open,
            });
          }
        } else if (code) {
          const res = await api.get(`/caisse/status?magasin_id=${code}`);
          if (!cancelled) setOpen(!!res.data?.is_open);
        }
      } catch {
        if (!cancelled) setOpen(isAll ? { M1: false, M2: false } : false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [api, user, code, isAll]);

  if (!shouldShowCaisseInHeader(user)) return null;
  if (loading) {
    return (
      <span className="hidden lg:inline-flex px-2.5 py-1 rounded-lg text-xs text-gray-500 border border-white/10" data-testid="caisse-header-chip">
        Caisse…
      </span>
    );
  }

  if (isAll && open && typeof open === 'object') {
    const allOpen = open.M1 && open.M2;
    const anyOpen = open.M1 || open.M2;
    return (
      <Link to="/dashboard" className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors hover:bg-white/5"
        style={{
          borderColor: anyOpen ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)',
          color: anyOpen ? '#86efac' : '#fca5a5',
          backgroundColor: anyOpen ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
        }}
        data-testid="caisse-header-chip"
        title={`M1: ${open.M1 ? 'ouverte' : 'fermée'} · M2: ${open.M2 ? 'ouverte' : 'fermée'}`}>
        {allOpen ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
        <span>M1 {open.M1 ? '✓' : '✗'} · M2 {open.M2 ? '✓' : '✗'}</span>
      </Link>
    );
  }

  const isOpen = !!open;
  return (
    <Link to={isOpen ? `/fermer-caisse?magasin_id=${encodeURIComponent(code || 'M1')}` : `/ouvrir-caisse?magasin_id=${encodeURIComponent(code || 'M1')}`} className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors hover:bg-white/5"
      style={{
        borderColor: isOpen ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)',
        color: isOpen ? '#86efac' : '#fca5a5',
        backgroundColor: isOpen ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
      }}
      data-testid="caisse-header-chip">
      {isOpen ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
      <span>Caisse {code} {isOpen ? 'ouverte' : 'fermée'}</span>
    </Link>
  );
};

export default CaisseHeaderChip;
